import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Video, FileText, UploadCloud, Loader2, Trash2, Edit3, CheckCircle2, Circle } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const LESSON_TYPES = [
  { v: "video", l: "Video" }, { v: "pdf", l: "PDF" }, { v: "ppt", l: "PPT" },
  { v: "text", l: "Text" }, { v: "quiz", l: "Quiz (Phase 7)" },
];

export default function FacultyContentManagerPage() {
  const { courseId } = useParams();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessonDlg, setLessonDlg] = useState(null); // {module_id, lesson?}
  const [lessonForm, setLessonForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadDlg, setUploadDlg] = useState(null); // {lesson, kind}
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/learning-content`);
      setModules(data.data.modules);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const openAddLesson = (moduleId) => {
    setLessonForm({ module_id: moduleId, lesson_title: "", lesson_type: "text", lesson_description: "",
      content_text: "", content_url: "", estimated_duration_minutes: 10, display_order: 1,
      is_preview: false, completion_required: true, minimum_watch_percentage: 99, download_allowed: false });
    setLessonDlg({ module_id: moduleId, create: true });
  };
  const openEditLesson = (lesson) => {
    setLessonForm({ ...lesson });
    setLessonDlg({ lesson, create: false });
  };

  const submitLesson = async () => {
    setSaving(true);
    try {
      if (lessonDlg.create) {
        await api.post(`/admin/courses/${courseId}/modules/${lessonDlg.module_id}/lessons`, lessonForm);
        toast.success("Lesson created.");
      } else {
        const body = { ...lessonForm };
        delete body.id; delete body.module_id; delete body.course_id; delete body.created_at; delete body.updated_at;
        delete body.created_by; delete body.updated_by; delete body.deleted_at; delete body.content_file_path;
        delete body.video_duration_seconds; delete body.resources_count;
        await api.put(`/admin/lessons/${lessonDlg.lesson.id}`, body);
        toast.success("Lesson updated.");
      }
      setLessonDlg(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const publishLesson = async (lesson) => {
    try {
      await api.patch(`/admin/lessons/${lesson.id}/status`, { status: lesson.status === "published" ? "draft" : "published" });
      toast.success("Lesson status updated.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitUpload = async () => {
    if (!uploadFile) return toast.error("Select a file.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      const endpoint = uploadDlg.kind === "video" ? "upload-video" : "upload-document";
      await api.post(`/admin/lessons/${uploadDlg.lesson.id}/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded.");
      setUploadDlg(null); setUploadFile(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  if (loading) return <DashboardLayout title="Course content"><LoadingBlock label="Loading content…" /></DashboardLayout>;

  return (
    <DashboardLayout title="Manage learning content" breadcrumbs={["Faculty", "Courses", "Content"]}>
      <div className="space-y-4" data-testid="faculty-content-manager">
        <Link to="/app/faculty/courses" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#101876]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to My Courses
        </Link>

        {modules.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            No modules yet. Ask Super Admin to create modules first, or go to <Link to={`/app/super-admin/courses/${courseId}`} className="text-[#101876] font-semibold hover:underline">course editor</Link>.
          </div>
        ) : modules.map((m) => (
          <div key={m.id} className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-slate-900">{m.display_order}. {m.module_title}</div>
                <div className="text-xs text-slate-500">{m.estimated_duration_minutes} min · {m.lessons?.length || 0} lessons</div>
              </div>
              <button onClick={() => openAddLesson(m.id)} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid={`faculty-add-lesson-${m.id}`}>
                <Plus className="h-3.5 w-3.5" /> Add lesson
              </button>
            </div>
            <div className="space-y-2">
              {(m.lessons || []).map((l) => (
                <div key={l.id} className="rounded-md border border-slate-100 bg-slate-50/50 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      {l.status === "published" ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" /> : <Circle className="mt-0.5 h-4 w-4 text-slate-400" />}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{l.display_order}. {l.lesson_title}</div>
                        <div className="mt-0.5 flex items-center gap-2 text-[10px]">
                          <span className="rounded bg-slate-200 px-1.5 py-0.5 font-bold uppercase text-slate-700">{l.lesson_type}</span>
                          <span className="text-slate-500">{l.estimated_duration_minutes}m</span>
                          {l.content_file_path && <span className="text-emerald-600 font-semibold">✓ File uploaded</span>}
                          {l.content_url && <span className="text-blue-600 font-semibold">↗ URL set</span>}
                          {l.content_text && <span className="text-slate-500">✓ Text set</span>}
                          {l.is_preview && <span className="text-amber-600 font-bold">FREE PREVIEW</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(l.lesson_type === "video" || ["pdf", "ppt", "pptx", "doc", "docx"].includes(l.lesson_type)) && (
                        <button onClick={() => { setUploadDlg({ lesson: l, kind: l.lesson_type === "video" ? "video" : "document" }); setUploadFile(null); }}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-[#101876]/40" data-testid={`faculty-upload-${l.id}`}>
                          <UploadCloud className="h-3 w-3" /> {l.content_file_path ? "Replace" : "Upload"}
                        </button>
                      )}
                      <button onClick={() => openEditLesson(l)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100" data-testid={`faculty-edit-lesson-${l.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => publishLesson(l)} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:border-[#329112]/40">
                        {l.status === "published" ? "Unpublish" : "Publish"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Lesson form dialog */}
      <Dialog open={!!lessonDlg} onOpenChange={(o) => !o && setLessonDlg(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>{lessonDlg?.create ? "Add lesson" : "Edit lesson"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Title *</Label><Input value={lessonForm.lesson_title || ""} onChange={(e) => setLessonForm({ ...lessonForm, lesson_title: e.target.value })} data-testid="faculty-lesson-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={lessonForm.lesson_type} onValueChange={(v) => setLessonForm({ ...lessonForm, lesson_type: v })}>
                  <SelectTrigger data-testid="faculty-lesson-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{LESSON_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Duration (min)</Label><Input type="number" value={lessonForm.estimated_duration_minutes || 0} onChange={(e) => setLessonForm({ ...lessonForm, estimated_duration_minutes: parseInt(e.target.value || "0", 10) })} /></div>
            </div>
            <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={lessonForm.lesson_description || ""} onChange={(e) => setLessonForm({ ...lessonForm, lesson_description: e.target.value })} /></div>
            {lessonForm.lesson_type === "text" && (
              <div className="space-y-1"><Label>Text content</Label><Textarea rows={6} value={lessonForm.content_text || ""} onChange={(e) => setLessonForm({ ...lessonForm, content_text: e.target.value })} data-testid="faculty-lesson-text" /></div>
            )}
            {lessonForm.lesson_type === "video" && (
              <div className="space-y-1"><Label>External video URL (optional if uploading file)</Label><Input value={lessonForm.content_url || ""} onChange={(e) => setLessonForm({ ...lessonForm, content_url: e.target.value })} placeholder="https://youtube.com/…" /></div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-2 text-xs">
                <span>Preview lesson</span>
                <Switch checked={!!lessonForm.is_preview} onCheckedChange={(v) => setLessonForm({ ...lessonForm, is_preview: v })} />
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50/60 p-2 text-xs">
                <span>Completion required</span>
                <Switch checked={!!lessonForm.completion_required} onCheckedChange={(v) => setLessonForm({ ...lessonForm, completion_required: v })} />
              </div>
              {lessonForm.lesson_type === "video" && (
                <div className="space-y-1 col-span-2"><Label>Min watch % for auto-complete</Label><Input type="number" value={lessonForm.minimum_watch_percentage || 100} onChange={(e) => setLessonForm({ ...lessonForm, minimum_watch_percentage: parseInt(e.target.value || "0", 10) })} /></div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setLessonDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitLesson} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid="faculty-lesson-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {lessonDlg?.create ? "Create" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={!!uploadDlg} onOpenChange={(o) => !o && setUploadDlg(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Upload {uploadDlg?.kind === "video" ? "video" : "document"} — {uploadDlg?.lesson?.lesson_title}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-slate-500">
              {uploadDlg?.kind === "video" ? "MP4 / WEBM / MOV up to 500 MB" : "PDF / PPT / PPTX / DOC / DOCX up to 50 MB"}
            </div>
            <input type="file"
              accept={uploadDlg?.kind === "video" ? ".mp4,.webm,.mov" : ".pdf,.ppt,.pptx,.doc,.docx"}
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full rounded-md border border-slate-200 bg-slate-50/60 p-2 text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-[#101876] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white"
              data-testid="faculty-upload-file"
            />
            {uploadFile && <div className="text-xs text-slate-500">{uploadFile.name} · {Math.ceil(uploadFile.size / 1024)} KB</div>}
          </div>
          <DialogFooter>
            <button onClick={() => setUploadDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitUpload} disabled={uploading || !uploadFile} className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-semibold text-white hover:bg-[#256a0d] disabled:opacity-70" data-testid="faculty-upload-submit">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              Upload
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
